// Ads results card: renders an ads run's ```ads-result block (see lib/adsResult.ts)
// in place of that block. Client name + review page link, the ads as 4:5 tiles
// (tap one for the full-size preview with Download), why the launch ads were
// picked, what the client must confirm, the message for the client with Copy,
// and how the helpers finished. Every value arrives validated and is rendered
// as text; images come only from the reply's own attached files.

import { useEffect, useMemo, useRef, useState } from "react";
import { ExternalLink } from "react-feather";
import { useSession } from "../store/session";
import { useAuthedImageUrl } from "../lib/useAuthedImageUrl";
import { collectGallery } from "../lib/lightbox-gallery";
import { copyTextToClipboard } from "../lib/clipboard";
import { adIsLaunch, fileBasename, helpersLine, whyHeading, type AdsResult, type AdsResultAd } from "../lib/adsResult";
import "../styles/ads-result.css";

interface Props {
  result: AdsResult;
  /** The attached image file for each ad, by index (from matchAdImages). */
  images: readonly (string | undefined)[];
}

export default function AdsResultCard({ result, images }: Props) {
  const launchIds = useMemo(() => new Set(result.launch.map(item => item.id)), [result.launch]);
  const launchCount = result.ads.filter(ad => adIsLaunch(ad, launchIds)).length;
  const reasons = result.launch.filter(item => item.why);
  const summary = [`${result.ads.length} ${result.ads.length === 1 ? "ad" : "ads"}`,
    launchCount > 0 ? `${launchCount} to launch` : ""].filter(Boolean).join(" · ");

  return (
    <section className="adsCard" aria-label={`Ads for ${result.client}`}>
      <header className="adsCard__head">
        <div className="adsCard__titles">
          <h3 className="adsCard__title">{result.client}</h3>
          <p className="adsCard__sub">{summary}</p>
        </div>
        {result.reviewUrl && (
          <a className="adsCard__open" href={result.reviewUrl} target="_blank" rel="noopener noreferrer">
            Open review page
            <ExternalLink width={14} height={14} aria-hidden="true" />
          </a>
        )}
      </header>

      {/* data-lb-group: a tapped ad opens the card's images as one gallery. */}
      <ul className="adsCard__grid" data-lb-group aria-label="Ads">
        {result.ads.map((ad, index) => (
          <AdTile key={`${index}-${ad.id}`} ad={ad} fileRef={images[index]} launch={adIsLaunch(ad, launchIds)} />
        ))}
      </ul>

      {reasons.length > 0 && (
        <section className="adsCard__section">
          <h4 className="adsCard__label">{whyHeading(reasons.length)}</h4>
          <ul className="adsCard__why">
            {reasons.map((item, index) => (
              <li key={`${index}-${item.id}`}>
                <span className="adsCard__whyId">{item.id}</span>
                <span>{item.why}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {result.clientMustConfirm.length > 0 && (
        <section className="adsCard__section">
          <h4 className="adsCard__label">Client must confirm</h4>
          <ul className="adsCard__confirm">
            {result.clientMustConfirm.map((item, index) => (
              <li key={index}>
                <span className="adsCard__box" aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {result.clientMessage && <ClientMessage text={result.clientMessage} />}

      {result.notes && <p className="adsCard__notes">{result.notes}</p>}

      {result.helpers && (
        <footer className={"adsCard__foot" + (result.helpers.failed > 0 ? " is-failed" : "")}>
          {helpersLine(result.helpers)}
        </footer>
      )}
    </section>
  );
}

function AdTile({ ad, fileRef, launch }: { ad: AdsResultAd; fileRef?: string; launch: boolean }) {
  const openLightbox = useSession(s => s.openLightbox);
  // Undefined skips the fetch: a tile without a matching file shows its title.
  const image = useAuthedImageUrl(fileRef);
  const filename = fileRef ? fileBasename(fileRef) : undefined;
  const dropped = ad.status === "dropped";
  const className = "adsTile" + (launch ? " is-launch" : "") + (dropped ? " is-dropped" : "");
  // No matching file (or it failed to load): the title stands in for the picture.
  const titleInFrame = !fileRef || image.status === "error";

  const details = (
    <span className="adsTile__body">
      <span className="adsTile__meta">
        <span className="adsTile__id">{ad.id}</span>
        {launch && <span className="adsTile__badge">Launch</span>}
        {!launch && ad.status === "reserve" && <span className="adsTile__state">Reserve</span>}
        {dropped && <span className="adsTile__state">Dropped</span>}
        {ad.score !== undefined && <span className="adsTile__score">Score {ad.score}</span>}
      </span>
      {!titleInFrame && ad.title && <span className="adsTile__title">{ad.title}</span>}
    </span>
  );

  if (fileRef && image.status === "loaded" && image.url) {
    return (
      <li className={className}>
        <button
          type="button"
          className="adsTile__card"
          title="View full size"
          onClick={(event) => {
            const gallery = collectGallery(event.currentTarget, { blobUrl: image.url!, filename });
            openLightbox(gallery.items, gallery.index);
          }}
        >
          <span className="adsTile__frame">
            <img src={image.url} alt="" draggable={false} decoding="async" data-lb-src={image.url} data-lb-name={filename} />
          </span>
          {details}
        </button>
      </li>
    );
  }

  return (
    <li className={className}>
      <div className="adsTile__card">
        <span className="adsTile__frame is-empty">
          {titleInFrame ? (
            <span className="adsTile__placeholder">
              {ad.title || ad.id}
              {fileRef && <span className="adsTile__missing">Image unavailable</span>}
            </span>
          ) : <span className="spinner" aria-hidden="true" />}
        </span>
        {details}
      </div>
    </li>
  );
}

function ClientMessage({ text }: { text: string }) {
  const [copy, setCopy] = useState<"idle" | "copied" | "failed">("idle");
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  async function copyMessage() {
    const ok = await copyTextToClipboard(text);
    setCopy(ok ? "copied" : "failed");
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopy("idle"), 1800);
  }

  // The Markdown copy block's own classes, so it looks and behaves like one.
  return (
    <div className="md adsCard__message">
      <div className="md-code md-copyQuote">
        <div className="md-code__bar">
          <span className="md-code__label">Message for the client</span>
          <button
            type="button"
            className={"md-code__copy" + (copy === "copied" ? " is-copied" : "")}
            onClick={() => void copyMessage()}
            aria-label={copy === "copied" ? "Message copied" : copy === "failed" ? "Copy failed. Try again" : "Copy the message for the client"}
          >
            {copy === "copied" ? "Copied" : copy === "failed" ? "Try again" : "Copy"}
          </button>
        </div>
        <blockquote className="adsCard__messageText">{text}</blockquote>
      </div>
    </div>
  );
}
